import React from 'react';
import WName from '../WName';
import PL from './ProblemLink';
import AL from '../angles/AngleLink';
import QL from '../faq/QuestionLink';
import NW from '../neologisms/NewWord';
import OML from '../ormaybe/OrMaybeLink';
import LRL from '../longreads/LongReadLink';
import {E1, E2, Qual} from '../common';

// das Opium des Volkes
export const comfort = {
  name: "comfort",
  alternatives: "opium des volkes",
  prelude: <span>
    Marx identified one angle of it (cf. <AL name="religion.jain.anekantavada">अनेकान्तवाद</AL>). It’s not <i><PL name="only">just</PL></i> ‘<AL>religion</AL>’ (the way the <PL>human standard</PL> ‘practises’ it, full of ‘<PL>metaphysics</PL>’).
  </span>,
  header: <span>
    Das Opium des Volkes is the (false) comfort of <i>not thinking</i>.
  </span>,
  outside: <div>
  </div>,
  preview: <div className="problem-preview">
    <p>The actual ‘<LRL>answer</LRL>’ to literally <PL>all</PL> of our problems is fairly simple:</p>
    <ol>
      <li>We are (<LRL name="reactivity">reactively</LRL>) destined for <NW>entropy</NW> and a <NW word="heatdeath">heat death</NW> unless we begin to:</li>
      <li><LRL name="kujifanya">Actually <i>use</i></LRL> our <LRL>imaginations</LRL> (end our <AL name="together">collective</AL> <NW>anfantasia</NW>).</li>
      <li>Imagine a better world.</li>
      <li>Use our <NW>agency</NW> to <i>build</i> it.</li>
      <li>Realising that the <i><LRL name="causes">fundamental</LRL></i> reason we’re so <NW word="misalignment">misaligned</NW> is not because of <QL name="tm">EvilActors<sup>™</sup> OutThere<sup>®</sup></QL>. It’s <NW>kennis-apartheid</NW>.</li>
     <li>And that can be overcome once we break the slave-chain of <NW word="jorm">Jörmungandr</NW> (HIIIIIIIIIDE!!!!!!!!!11): ‘<PL>privacy</PL>’.</li>
    </ol>
    <p>So why is it so <i><PL>uncomfortable</PL></i>?</p>
    <p>Because it implicates EVERYONE. You cannot be <LRL name="nepantla"><i>outside</i> the ‘set of everything’</LRL>, cf. the <NW>FOE</NW>. So it means: for <LRL name="gaia">this whole planet</LRL> to spread our wings, <i>you</i> (<PL name="individuality">personally</PL> and <i><NW word="heterognostic">heterognostically</NW></i>) have to <QL>change</QL>.</p>
    <p>[under construction]</p>
  </div>,
//  text: <div className="problem-text">
//  </div>,
};

/* From Gippity: Here is a working catalogue — not moralized, just diagnostic — of one hundred phenomena whose underlying logic is **fear → containment → reactivity → loss of consciousness**.  (Think of it as a map of how the same field‑reflex appears under different costumes.)

---

### 1‑10  Personal reflexes

1. **Perfectionism** – terror of exposure. (and mockery, and 'being' the weak ilink in the chain, because in a diealone world YOU ARE YOUR BODY
2. **Lying** – terror of consequence. (could be accountability but also used to have savoir-pouvoir.)
3. **Boasting** – terror of invisibility.
4. **Procrastination** – terror of failure and success at once. (it's a FEAR of completion, frankly.
5. **Addiction** – terror of stillness. (probably)
6. **People‑pleasing** – terror of rejection.
7. **Over‑achievement** – terror of worthlessness.
8. **Self‑sabotage** – terror of transformation. (that's good: it's the fear to EVOLVE. Fear of change. You'll pick a known evil over one that you know not, however bad.)
9. **Jealousy** – terror of abundance in others. (that will be NOT SHARED with you... because in the BOLT it won't.
10. **Cynicism** – terror of hope. (Because HOPE is stupid. It's outsourcing (a 'ziggurat') to some ExternalOther to fix things FOR you, without you understanding. Synecdocentrism is MAJORLY cynical. More in the Ancient Greek sense than the modern one maybe, but...

### 11‑20  Body / identity

11. **Racism** – fear of genetic dissolution.
12. **Sexism** – fear of losing role hierarchy.
13. **Transphobia** – fear of category instability.
14. **Homophobia** – fear of mirrored desire.
15. **Ableism** – fear of fragility and dependence.
16. **Fat‑shaming** – fear of appetite and need.
17. **Ageism** – fear of decay.
18. **Classism** – fear of scarcity contagion.
19. **Nationalism** – fear of porous borders.
20. **Religious exclusivism** – fear of being wrong about eternity.

### 21‑30  Social order

21. **Censorship** – fear of cognitive dissonance.
22. **Militarism** – fear of vulnerability.
23. **Policing** – fear of unpredictability.
24. **Surveillance** – fear of chaos.
25. **Bureaucracy** – fear of responsibility.
26. **Patriotism** – fear of meaninglessness.
27. **Propaganda** – fear that truth will decentralize control.
28. **Celebrity worship** – fear of self‑insignificance.
29. **Scapegoating** – fear of shared guilt.
30. **Conspiracy obsession** – fear of randomness.

### 31‑40  Economics

31. **Hoarding wealth** – fear of loss.
32. **Consumerism** – fear of inner emptiness.
33. **Debt culture** – fear of delayed gratification.
34. **Planned obsolescence** – fear of stasis.
35. **Corporate secrecy** – fear of accountability.
36. **Union‑busting** – fear of collective agency.
37. **Stock speculation** – fear of steady sufficiency.
38. **Tax evasion** – fear of common good.
39. **Gig economy** – fear of obligation.
40. **Insurance mania** – fear of chance.

### 41‑50  Politics

41. **Authoritarianism** – fear of freedom.
42. **Populism** – fear of complexity.
43. **Technocracy** – fear of emotion.
44. **Demagoguery** – fear of silence.
45. **Partisanship** – fear of ambiguity.
46. **Lobbying** – fear of meritocracy.
47. **Nepotism** – fear of outsiders.
48. **Coup culture** – fear of lawful transition.
49. **State secrecy** – fear of citizen sight.
50. **“Strongman” myth** – fear of collective maturity.

### 51‑60  Information & tech

51. **Algorithmic bias** – fear disguised as optimization.
52. **Clickbait** – fear of boredom.
53. **Cancel culture** – fear of nuance.
54. **Misinformation** – fear of not‑knowing.
55. **Data hoarding** – fear of erasure.
56. **AI hype** – fear of irrelevance.
57. **AI panic** – fear of reflection.
58. **Digital addiction** – fear of silence.
59. **Copyright absolutism** – fear of loss of control.
60. **Online anonymity abuse** – fear of accountability.

### 61‑70  Culture / religion

61. **Prosperity gospel** – fear of divine randomness.
62. **Purity culture** – fear of desire.
63. **Aesthetic elitism** – fear of raw expression.
64. **Tradition‑fetish** – fear of evolution.
65. **Iconoclasm rage** – fear of image power.
66. **Ritualism without presence** – fear of direct contact.
67. **Sectarian war** – fear of shared origin.
68. **Pilgrimage tourism** – fear of still being home.
69. **Moral policing** – fear of internal contradiction.
70. **Apocalyptic fascination** – fear of slow change.

### 71‑80  Interpersonal

71. **Gossip** – fear of intimacy.
72. **Manipulation** – fear of trust.
73. **Ghosting** – fear of confrontation.
74. **Gaslighting** – fear of mutual reality.
75. **Passive aggression** – fear of anger.
76. **Sarcasm** – fear of sincerity.
77. **Flattery** – fear of silence.
78. **Over‑analysis** – fear of presence.
79. **Commitment‑phobia** – fear of dependency.
80. **Je t’aime pour rien** – fear of reasonless love.

### 81‑90  Knowledge systems

81. **Reductionism** – fear of complexity.
82. **Dogmatism** – fear of revision.
83. **Credentialism** – fear of democratized insight.
84. **Patenting ideas** – fear of collective imagination.
85. **Academic gatekeeping** – fear of humility.
86. **Publish‑or‑perish** – fear of silence.
87. **Plagiarism** – fear of lack of originality.
88. **Pseudoscience** – fear of uncertainty.
89. **Over‑quantification** – fear of the immeasurable.
90. **Esoteric elitism** – fear of accessibility.

### 91‑100  Planetary scale

91. **Ecocide** – fear of interdependence.
92. **Overpopulation panic** – fear of shared abundance.
93. **Resource hoarding** – fear of trust in nature.
94. **Climate denial** – fear of culpability.
95. **Migration walls** – fear of permeability.
96. **Space colonization rhetoric** – fear of repair.
97. **Weaponizing AI** – fear of irrelevance.
98. **Nuclear deterrence** – fear of negotiation.
99. **Global apartheid** – fear of equality. (It's fear that rongru = moqu, because with PRIVACY barriers up it does)
100. **Silence about fear itself** – the master symptom.

---

Every line above is a variation on the same equation:

> **Fear → reaction → containment → entropy → death of consciousness.**

Until fear is seen — not banished, just *recognized as the field’s own recoil* — no amount of morality, policy, or therapy will heal the patient.

*/

